import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { useEffect, useState, useContext } from 'react'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { SearchField } from '../components/SearchField'
import { useC216SubtenantDeviceGet } from '@/hooks/useC216SubtenantDeviceGet'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '@/hooks/useAuth'
import { errorMsg } from '@/constants/messages'
import { LanguageContext } from '@/util/LanguageContext'

interface CustomerInfo {
  layer1: string
  serial_number: string
  cotopat_id: string
  android_id: string
  plan: string
  device_type: string
  start_date: string
  end_date: string
  registration_date: string
  app_version: string
}

const formatUnixDate = (unix: number | null | undefined): string => {
  if (unix === null || unix === undefined) return '--'
  const d = new Date(unix * 1000)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
}

// 顧客情報管理画面
export const CustomerInfoManagement = () => {
  const auth = useAuth()
  const [allCustomers, setAllCustomers] = useState<CustomerInfo[]>([])
  const [displayCustomers, setDisplayCustomers] = useState<CustomerInfo[]>([])
  const [sortField, setSortField] = useState<keyof CustomerInfo | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const c216 = useC216SubtenantDeviceGet()
  const [openErrorDialog, setOpenErrorDialog] = useState(false)
  const [errorDialogText, setErrorDialogText] = useState('')
  const translation = useContext(LanguageContext)
  const loading = c216.loading === loadStatus.LOADING

  const getSubtenantDevice = async () => {
    const result = await c216.execSubtenantDeviceGet(auth.tenant)
    if (!result.success) {
      setErrorDialogText(result.message ?? errorMsg.SYSTEM_ERROR)
      setOpenErrorDialog(true)
      return
    }
    const customers: CustomerInfo[] = (result.info ?? []).flatMap(group => {
      if (!Array.isArray(group.devices) || group.devices.length === 0) {
        return [{
          layer1: group.tenant,
          serial_number: '',
          cotopat_id: '',
          android_id: '',
          plan: '',
          device_type: '',
          start_date: '',
          end_date: '',
          registration_date: '',
          app_version: '',
        }]
      }
      return group.devices.map(device => ({
        layer1: group.tenant,
        serial_number: device.license_id,
        cotopat_id: device.cotopat_id,
        android_id: device.device_id,
        plan: device.license_type,
        device_type: device.device_type,
        start_date: formatUnixDate(device.start_date),
        end_date: formatUnixDate(device.end_date),
        registration_date: formatUnixDate(device.registration_date),
        app_version: device.application_version,
      }))
    })
    setAllCustomers(customers)
    setDisplayCustomers(customers)
  }


  useEffect(() => {
    auth.setIsDisable(loading)
  }, [auth, loading])

  useEffect(() => {
    getSubtenantDevice()
  }, [])

  // 検索ハンドラ（固定データをフロントエンドでフィルタ）
  const searchHandler = (keyword: string) => {
    if (!keyword.trim()) {
      setDisplayCustomers(allCustomers)
      return
    }
    const filtered = allCustomers.filter(
      c =>
        c.cotopat_id.toLowerCase().includes(keyword.toLowerCase()) ||
        c.serial_number.toLowerCase().includes(keyword.toLowerCase()) ||
        c.android_id.toLowerCase().includes(keyword.toLowerCase()),
    )
    setDisplayCustomers(filtered)
  }

  const handleSort = (field: keyof CustomerInfo) => {
    const isAsc = sortField === field && sortDir === 'asc'
    setSortDir(isAsc ? 'desc' : 'asc')
    setSortField(field)
    setPage(0)
  }

  const sortedRows = [...displayCustomers].sort((a, b) => {
    if (!sortField) return 0
    const aVal = a[sortField]
    const bVal = b[sortField]
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const formatPlanLabel = (plan: string | null | undefined) => {
    if (!plan) return ''
    const p = plan.toLowerCase()
    if (p === 'subscription') return 'Subscription'
    if (p === 'term') return 'Term Plan'
    return plan
  }

  const formatDeviceType = (type: string | null | undefined) => {
    if (!type) return ''
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
  }

  const columns: { field: keyof CustomerInfo; label: string }[] = [
    { field: 'layer1', label: translation['label_customer_info_layer1'] },
    { field: 'serial_number', label: translation['label_customer_info_serial_number'] },
    { field: 'cotopat_id', label: translation['label_customer_info_cotopat_id'] },
    { field: 'android_id', label: translation['label_customer_info_android_id'] },
    { field: 'plan', label: translation['label_customer_info_plan'] },
    { field: 'start_date', label: translation['label_customer_info_start_date'] },
    { field: 'end_date', label: translation['label_customer_info_end_date'] },
    { field: 'registration_date', label: translation['label_customer_info_registration_date'] },
    { field: 'app_version', label: translation['label_app_version'] },
  ]

  const headerDividerSx = {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      borderRight: '1px solid #ccc',
    },
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ececec',
        pb: 8,
      }}
    >
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2 }}>
        <Typography sx={{ font: 'normal normal bold 34px/40px Noto Sans JP', color: '#202020' }}>
          Cotopat
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, maxWidth: 1160, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography sx={{ font: 'normal normal bold 28px/36px Noto Sans JP', color: '#202020', whiteSpace: 'pre-wrap' }}>
            {translation['label_device_info_management']}
          </Typography>
          <SearchField onKeyDown={searchHandler} />
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            backgroundColor: '#f7f7f7',
          }}
        >
          <TableContainer>
            <Table sx={{ '& .MuiTableCell-root': { px: 1.5 } }}>
              <TableHead>
                <TableRow>
                  {columns.map((col, i) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontWeight: 700,
                        borderBottom: '2px solid #3e3e3e',
                        ...(i < columns.length - 1 ? headerDividerSx : {}),
                      }}
                    >
                      <TableSortLabel
                        active={sortField === col.field}
                        direction={sortField === col.field ? sortDir : 'asc'}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row, i) => (
                  <TableRow key={`${row.layer1}-${row.android_id}-${i}`}>
                    {columns.map(col => (
                      <TableCell key={col.field}>
                        {col.field === 'plan' ? (
                          <>
                            {formatPlanLabel(row.plan)}<br />{formatDeviceType(row.device_type)}
                          </>
                        ) : row[col.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      </Box>

      <Footer />

      {openErrorDialog && (
        <ModalConfirmDialog
          open={openErrorDialog}
          text={errorDialogText}
          onClick={() => setOpenErrorDialog(false)}
        />
      )}

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            pointerEvents: 'auto',
            cursor: 'wait',
            zIndex: 1500,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  )
}
